<template>
<gkButton 
  :color="isFollow ? 'follow' : 'unfollow'" 
  :size="size"
  :label="isFollow ? unlabel : label"
  :data-follow="(isFollow | 0)"
  @click="handleFollow"
  v-if="!loginUser || loginUser.uid != id"
/>
</template>

<script>
import VueTypes from 'vue-types'
import {mapState} from 'vuex'
import gkButton from 'gkui/button'

import AccountService from '../../service/account.service'
import TopicService from '../../service/topic.service'

/**
 * 关注按钮
 * @desc 提供频道或作者的关注或取消的功能
 * @module component/FollowButton
 * @extends gkButton
 * @example
 * <FollowButton type="topic" :id="topicID" :follow="isFollow" @follow="followStatus" unlabel="已关注" />
 * <FollowButton type="user" :id="userID" :follow="isFollow" @follow="followStatus" unlabel="已关注" />
 */
export default {
  name: 'follow-button',
  /**
   * 引用的组件
   * @prop {Component}  gkButton  按钮组件
   */
  components: {
    gkButton
  },
  /**
   * 传入数据
   * @prop {String}         [size=small]       按钮 size
   * @prop {String}         [type=user]        关注的类型 [topic:频道, user:用户]
   * @prop {Number|String}  id                 频道/作者 ID
   * @prop {Boolean}        [follow=false]     是否已关注
   * @prop {String}         [label=关注]        未关注的文案
   * @prop {String}         [unlabel=取消关注]  已关注的文案
   */
  props: {
    size: VueTypes.string.def('small'),
    type: VueTypes.oneOf(['user', 'topic']).def('user'),
    id: VueTypes.oneOfType([Number, String]),
    follow: VueTypes.bool.def(false),
    label: VueTypes.string.def('关注'),
    unlabel: VueTypes.string.def('取消关注')
  },
  /**
   * 内部数据
   * @member
   * @prop {Boolean}  loading=false  是否在操作中
   * @prop {Boolean}  isFollow       是否已关注
   */
  data () {
    return {
      loading: false,
      isFollow: this.follow
    }
  },
  /**
   * 计算属性
   * @prop {Object} loginUser=null  已登录用户的信息
   */
  computed: {
    ...mapState(['loginUser'])
  },
  watch: {
    /**
     * 监听器 - follow 属性
     * 同步外部的关注状态
     * @function
     * @params 外部传入的 follow 值
     */
    follow (val) {
      this.isFollow = val
    }
  },
  methods: {
    /**
     * 关注/取消
     * 根据当前状态执行操作，成功后更新内部数据并 $emit "follow" 事件
     * @emits followService:handleFollow
     */
    handleFollow () {
      if (this.loading) {
        return
      }

      this.loading = true

      let defer = ''

      switch (this.type) {
        case 'topic':
          defer = TopicService.followTopic(this.id, !this.isFollow)
          break
        case 'user':
          defer = AccountService.followUser(this.id, !this.isFollow)
          break
        default:
          break
      }

      if (!defer) {
        return
      }

      /**
       * 执行关注/取消的操作
       * @event followService:handleFollow
       * @return {Boolean} 是否已关注
       */
      defer.then(res => {
        this.isFollow = !this.isFollow
        this.loading = false
        this.$emit('follow', this.isFollow)
      }).catch(() => {
        this.loading = false
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/css/common';

</style>
